import { flashToken } from '~/utils/flashAuth';
import { toApiError } from '~/utils/apiErrors';

interface IServiceFetchOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: Record<string, any>;
    headers?: Record<string, string>;
}

/** A file the service served: a Design Image, an aerial crop, the labels export. */
export interface IServiceFile {
    blob: Blob;
    /** From `Content-Disposition`, when the service names the file. */
    filename: string | null;
}

/** The signed-in user's token as a Bearer header, as quote-edit's `createAuthFetch` sends it; none on a laptop. */
const authorization = (headers: Record<string, string> = {}): Record<string, string> => {
    const token = flashToken();
    return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const apiError = (error: unknown) => {
    const { status, data } = error as { status?: number; data?: unknown };
    return toApiError(status ?? 0, data);
};

/** A refused file request's body arrives as a Blob: read the service's envelope out of it. */
const fileApiError = async (error: unknown) => {
    const { status, data } = error as { status?: number; data?: unknown };
    let body: unknown = data;
    if (data instanceof Blob) {
        try {
            body = JSON.parse(await data.text());
        } catch {
            body = null;
        }
    }
    return toApiError(status ?? 0, body);
};

/**
 * A `$fetch` for one backend service, the counterpart of quote-edit's `createAuthFetch`. Every failure becomes a
 * `SystemDesignApiError` for the page to show in place, never Nuxt's fatal error page. A 401 in the middle of a
 * session is shown too rather than sent to sign in: that would lose unsaved labels (the layout sends a visitor to
 * sign in before any page loads).
 */
export const createServiceFetch = (baseURL: string) => {
    const serviceFetch = $fetch.create({ baseURL });
    return async <T>(url: string, options: IServiceFetchOptions = {}): Promise<T> => {
        try {
            return (await serviceFetch(url, { ...options, headers: authorization(options.headers) })) as T;
        } catch (error) {
            throw apiError(error);
        }
    };
};

/**
 * Fetches a file with the same token. An `<img>`, an SVG `<image>` or a download link cannot send one, so the console
 * fetches files itself and shows them as `blob:` URLs.
 */
export const createServiceFileFetch = (baseURL: string) => {
    const serviceFetch = $fetch.create({ baseURL });
    return async (url: string): Promise<IServiceFile> => {
        try {
            const response = await serviceFetch.raw(url, {
                headers: authorization(),
                responseType: 'blob' as const,
            });
            const disposition = response.headers.get('content-disposition') ?? '';
            const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? null;
            return { blob: response._data as Blob, filename };
        } catch (error) {
            throw await fileApiError(error);
        }
    };
};
